import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, Plus, Save, Dumbbell, Youtube, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { SubstitutePicker } from '../SubstitutePicker';
import { ExerciseCard } from './ExerciseCard';
import { ExercisePagination, ExerciseRangeInfo, PageSizeSelect, PAGE_SIZE_OPTIONS } from './ExercisePagination';
import { getYoutubeEmbedUrl } from './YoutubeLite';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE_KEY = 'ferraz_exercise_page_size';
const EMPTY_FORM = { name: '', youtube_url: '', instructions: '', category: '', substitute_ids: [] };
const DEFAULT_CATEGORIES = ['Peito', 'Costas', 'Pernas', 'Ombros', 'Bíceps', 'Tríceps', 'Abdômen', 'Glúteos', 'Cardio'];

const readPageSize = () => {
  const v = Number(localStorage.getItem(PAGE_SIZE_KEY));
  return PAGE_SIZE_OPTIONS.includes(v) ? v : 20;
};

const queryKey = (page, pageSize, search, category) => `${page}|${pageSize}|${search}|${category}`;

const fetchExercisePage = async ({ page, pageSize, search, category, focusId }) => {
  const params = { page, page_size: pageSize };
  if (search) params.search = search;
  if (category) params.category = category;
  if (focusId) params.focus_id = focusId;
  const res = await axios.get(`${API}/api/admin/exercises/paged`, { params });
  return res.data;
};

const CategorySuggestions = ({ categories }) => (
  <datalist id="category-suggestions">
    {[...new Set([...categories, ...DEFAULT_CATEGORIES])].map((cat) => (
      <option key={cat} value={cat} />
    ))}
  </datalist>
);

const NewExerciseForm = ({ options, onCreated, onCancel }) => {
  const [data, setData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const embed = getYoutubeEmbedUrl(data.youtube_url);
  const set = (key) => (e) => setData((d) => ({ ...d, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.name.trim()) { toast.error('Nome do exercício é obrigatório'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/admin/exercises`, { ...data, name: data.name.trim() });
      toast.success('Exercício criado com sucesso!');
      await onCreated(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar exercício');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      className="bg-zinc-900/50 border border-neon-lime/30 backdrop-blur-md rounded-2xl p-6"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="new-exercise-form"
    >
      <h2 className="font-primary font-bold text-2xl text-white uppercase mb-4">Novo Exercício</h2>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-zinc-400 mb-2">Nome do Exercício *</label>
          <Input value={data.name} onChange={set('name')} placeholder="Ex: Supino Reto" className="bg-zinc-950/50 border-zinc-800 focus:border-neon-lime text-white h-12" required data-testid="new-exercise-name" />
        </div>
        <div>
          <label className="block text-sm font-medium text-zinc-400 mb-2">Categoria</label>
          <Input value={data.category} onChange={set('category')} placeholder="Ex: Peito, Costas, Pernas..." className="bg-zinc-950/50 border-zinc-800 focus:border-neon-lime text-white h-12" list="category-suggestions" data-testid="new-exercise-category" />
        </div>
        <div>
          <label className="block text-sm font-medium text-zinc-400 mb-2">Link do YouTube</label>
          <div className="relative">
            <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
            <Input type="url" value={data.youtube_url} onChange={set('youtube_url')} placeholder="https://youtube.com/watch?v=..." className="pl-10 bg-zinc-950/50 border-zinc-800 focus:border-neon-lime text-white h-12" data-testid="new-exercise-youtube" />
          </div>
          {embed && (
            <div className="mt-3 aspect-video rounded-lg overflow-hidden bg-black max-w-sm">
              <iframe src={embed} title="Preview" className="w-full h-full" frameBorder="0" allowFullScreen />
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-zinc-400 mb-2">Instruções</label>
          <textarea value={data.instructions} onChange={set('instructions')} placeholder="Descreva como executar o exercício..." className="w-full bg-zinc-950/50 border border-zinc-800 text-white rounded-lg p-3 min-h-[100px] focus:border-neon-lime focus:outline-none" data-testid="new-exercise-instructions" />
        </div>
        <SubstitutePicker
          selectedIds={data.substitute_ids}
          onChange={(ids) => setData((d) => ({ ...d, substitute_ids: ids }))}
          library={options}
          currentExerciseName={data.name}
        />
        <div className="flex gap-3">
          <Button type="submit" disabled={saving} className="flex-1 bg-neon-lime text-black font-black uppercase hover:bg-neon-lime/80" data-testid="new-exercise-save">
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Salvando...' : 'Salvar Exercício'}
          </Button>
          <Button type="button" onClick={onCancel} className="bg-zinc-800 text-white hover:bg-zinc-700" data-testid="new-exercise-cancel">
            Cancelar
          </Button>
        </div>
      </form>
    </motion.div>
  );
};

const ExercisesTab = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(readPageSize);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [result, setResult] = useState({ items: [], total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [options, setOptions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const skipKeyRef = useRef(null);
  const listTopRef = useRef(null);

  const loadMeta = useCallback(async () => {
    const [cat, opt] = await Promise.allSettled([
      axios.get(`${API}/api/admin/exercises/categories`),
      axios.get(`${API}/api/admin/exercises/options`),
    ]);
    if (cat.status === 'fulfilled') setCategories(cat.value.data);
    if (opt.status === 'fulfilled') setOptions(opt.value.data);
  }, []);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  // Debounced search; any new filter returns to page 1
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      if (searchInput.trim() !== search) setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput, search]);

  useEffect(() => {
    const key = queryKey(page, pageSize, search, category);
    if (skipKeyRef.current === key) { skipKeyRef.current = null; return; }
    let cancelled = false;
    setLoading(true);
    fetchExercisePage({ page, pageSize, search, category })
      .then((data) => {
        if (cancelled) return;
        setResult(data);
        if (data.page !== page) { skipKeyRef.current = queryKey(data.page, pageSize, search, category); setPage(data.page); }
      })
      .catch(() => { if (!cancelled) toast.error('Erro ao carregar exercícios'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, pageSize, search, category]);

  useEffect(() => {
    if (!highlightId) return undefined;
    const el = document.querySelector(`[data-testid="exercise-card-${highlightId}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const t = setTimeout(() => setHighlightId(null), 4000);
    return () => clearTimeout(t);
  }, [highlightId, result]);

  const goToPage = (p) => {
    setEditingId(null);
    setPage(p);
    listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const changePageSize = (size) => {
    try { localStorage.setItem(PAGE_SIZE_KEY, String(size)); } catch (e) { console.warn('[ExercisesTab] non-critical failure', e); }
    setPageSize(size);
    setPage(1);
  };

  const replaceItem = (updated) => {
    setResult((r) => ({ ...r, items: r.items.map((it) => (it.id === updated.id ? updated : it)) }));
    setOptions((opts) => opts.map((o) => (o.id === updated.id ? { id: updated.id, name: updated.name, category: updated.category } : o)));
  };

  const handleCreated = async (created) => {
    // Clear filters so the new exercise is visible, then jump to its alphabetical page
    const data = await fetchExercisePage({ page: 1, pageSize, search: '', category: '', focusId: created.id });
    const nextKey = queryKey(data.page, pageSize, '', '');
    if (nextKey !== queryKey(page, pageSize, search, category)) skipKeyRef.current = nextKey;
    setSearchInput('');
    setSearch('');
    setCategory('');
    setPage(data.page);
    setResult(data);
    setShowForm(false);
    setHighlightId(created.id);
    loadMeta();
  };

  const handleUpdate = async (id, payload) => {
    try {
      const res = await axios.patch(`${API}/api/admin/exercises/${id}`, payload);
      replaceItem(res.data);
      setEditingId(null);
      toast.success('Exercício atualizado!');
      if (payload.category && !categories.includes(payload.category)) loadMeta();
    } catch (error) {
      toast.error('Erro ao atualizar exercício');
    }
  };

  const handleDelete = async (ex) => {
    if (!window.confirm(`Excluir o exercício "${ex.name}"?`)) return;
    try {
      await axios.delete(`${API}/api/admin/exercises/${ex.id}`);
      toast.success('Exercício excluído');
      setOptions((opts) => opts.filter((o) => o.id !== ex.id));
      const data = await fetchExercisePage({ page, pageSize, search, category });
      setResult(data);
      if (data.page !== page) { skipKeyRef.current = queryKey(data.page, pageSize, search, category); setPage(data.page); }
    } catch (error) {
      toast.error('Erro ao excluir exercício');
    }
  };

  const hasFilters = Boolean(search || category);

  return (
    <div className="space-y-4" data-testid="exercises-tab-content">
      <CategorySuggestions categories={categories} />
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <Input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Buscar exercício..."
            className="pl-10 bg-zinc-950/50 border-zinc-800 focus:border-neon-lime text-white h-12"
            data-testid="exercise-search-input"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <select
            value={category}
            onChange={(e) => { setCategory(e.target.value); setPage(1); }}
            className="pl-10 pr-8 h-12 w-full bg-zinc-950/50 border border-zinc-800 rounded-lg text-white appearance-none cursor-pointer min-w-[180px]"
            data-testid="exercise-category-filter"
          >
            <option value="">Todas categorias</option>
            {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-neon-lime text-black font-black uppercase hover:bg-neon-lime/80 h-12" data-testid="new-exercise-btn">
          <Plus className="w-5 h-5 mr-2" />
          Novo Exercício
        </Button>
      </div>

      {showForm && <NewExerciseForm options={options} onCreated={handleCreated} onCancel={() => setShowForm(false)} />}

      <div ref={listTopRef} className="bg-zinc-900/50 border border-white/5 rounded-2xl p-4 sm:p-6 scroll-mt-24">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <Dumbbell className="w-8 h-8 text-neon-lime" />
            <div>
              <h3 className="font-primary font-bold text-xl text-white">Banco de Exercícios</h3>
              <ExerciseRangeInfo page={result.page || page} pageSize={pageSize} total={result.total} />
            </div>
          </div>
          <PageSizeSelect value={pageSize} onChange={changePageSize} />
        </div>

        {loading && result.items.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="exercise-list-loading">
            {Array.from({ length: 4 }, (_, i) => (
              <div key={`sk-${i}`} className="h-56 rounded-xl bg-zinc-950/50 border border-white/5 animate-pulse" />
            ))}
          </div>
        ) : result.items.length === 0 ? (
          <div className="text-center py-12" data-testid="exercise-list-empty">
            <Dumbbell className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-white mb-2">Nenhum exercício encontrado</h3>
            <p className="text-zinc-500">{hasFilters ? 'Tente ajustar os filtros de busca' : 'Adicione seu primeiro exercício à biblioteca'}</p>
          </div>
        ) : (
          <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 items-start transition-opacity duration-200 ${loading ? 'opacity-50' : ''}`} data-testid="exercise-list">
            {result.items.map((ex) => (
              <ExerciseCard
                key={ex.id}
                ex={ex}
                editing={editingId === ex.id}
                highlighted={highlightId === ex.id}
                options={options}
                onStartEdit={() => setEditingId(ex.id)}
                onCancelEdit={() => setEditingId(null)}
                onSave={handleUpdate}
                onDelete={() => handleDelete(ex)}
                onItemChange={replaceItem}
              />
            ))}
          </div>
        )}

        <ExercisePagination page={result.page || page} totalPages={result.total_pages} onPageChange={goToPage} disabled={loading} />
      </div>
    </div>
  );
};

export default ExercisesTab;
